/**
 * Family Service Client
 *
 * Provides a centralized client for all family-related operations.
 * Replaces direct Supabase calls with API calls to the family microservice.
 *
 * Features:
 * - Type-safe operations for family groups and members
 * - Automatic error handling
 * - Support for family goals and dashboard
 */

import { API_ENDPOINTS } from "@/config/api";
import {
  isRateLimitError,
  isTransientClientFetchError,
  rateLimitRetryDelayMs,
  sleepMs,
} from "@/utils/apiClientErrors";
import { getAuthToken, resolveApiRequestHeaders } from "./assets/assetHelpers";

function appendQueryString(path: string, queryString: string): string {
  return queryString ? `${path}?${queryString}` : path;
}

export interface Family {
  id: string;
  primary_user_id: string;
  name: string;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

export interface FamilyMember {
  id: string;
  family_id: string;
  user_id: string | null;
  full_name: string;
  relationship: string;
  pan_number: string;
  role: "ADMIN" | "SPOUSE" | "CHILD" | "DEPENDENT";
  date_of_birth: string | null;
  is_active: boolean;
  is_deceased?: boolean;
  deceased_at?: string | null;
  deceased_reason?: string | null;
  deceased_notes?: string | null;
  generation_tag?: string | null;
  created_at: string;
  updated_at: string;
}

export interface FamilyGoal {
  id: string;
  family_id: string;
  goal_name: string;
  target_amount: number;
  current_amount: number;
  owner_member_id?: string | null;
  last_milestone_percent?: number | null;
  target_date: string | null;
  category: string | null;
  description: string | null;
  currency: string;
  priority?: string | null;
  monthly_contribution_amount?: number | null;
  sip_auto_enabled?: boolean | null;
  sip_day_of_month?: number | null;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

export interface FamilyGoalContributionMember {
  id: string;
  full_name: string;
  relationship: string;
  user_id: string;
  is_active?: boolean;
}

export interface FamilyGoalContribution {
  id: string;
  goal_id: string;
  member_id: string;
  contribution_percentage: number | null;
  actual_contribution: number;
  created_at: string;
  updated_at: string;
  member: FamilyGoalContributionMember | null;
}

export interface FamilyLicense {
  id: string;
  family_id: string;
  license_type: string;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface FamilyInvitation {
  id: string;
  family_id: string;
  email: string;
  full_name: string;
  relationship: string;
  role: string;
  token: string;
  status: string;
  expires_at: string;
  created_at: string;
}

export interface FamilyDashboard {
  total_members: number;
  active_members: number;
  total_goals: number;
  active_goals: number;
  total_assets: number;
  total_liabilities: number;
  net_worth: number;
}

export interface MemberLimitCheck {
  current_members: number;
  max_allowed: number;
  can_add_more: boolean;
  needs_upgrade: boolean;
}

export interface FamilyEventReminder {
  id: string;
  event_id: string;
  remind_at: string;
  channel: "in_app" | "email" | "push";
  status: "pending" | "sent" | "cancelled";
  created_by?: string | null;
  created_at: string;
}

export interface FamilyEvent {
  id: string;
  family_id: string;
  title: string;
  description: string | null;
  start_at: string;
  end_at: string | null;
  all_day: boolean;
  event_type: "custom" | "birthday" | "deadline" | "renewal";
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  source_type?: "goal_deadline" | "document_expiry" | null;
  source_id?: string | null;
  reminders?: FamilyEventReminder[];
}

export interface FamilyCalendarSyncResult {
  synced: number;
  removed: number;
  total_candidates: number;
}

export interface FamilyReminderPreferences {
  id: string;
  family_id: string;
  user_id: string;
  timezone: string;
  channels: ("in_app" | "email" | "push")[];
  created_at: string;
  updated_at: string;
}

export interface FamilyTask {
  id: string;
  family_id: string;
  title: string;
  description: string | null;
  due_at: string | null;
  assigned_to: string | null;
  created_by: string | null;
  status: "open" | "submitted" | "approved" | "rejected" | "completed";
  requires_approval: boolean;
  cooldown_minutes?: number;
  approved_by: string | null;
  approved_at: string | null;
  rejected_by: string | null;
  rejected_at: string | null;
  completed_by: string | null;
  completed_at: string | null;
  dispute_reason?: string | null;
  disputed_by?: string | null;
  disputed_at?: string | null;
  linked_workflow: string | null;
  created_at: string;
  updated_at: string;
}

export interface FamilyNote {
  id: string;
  family_id: string;
  note_type: "note" | "playbook";
  title: string;
  content_md: string;
  visibility: "family" | "admins" | "private";
  is_pinned: boolean;
  tags: string[];
  linked_entity_type?: "goal" | "asset" | "document" | null;
  linked_entity_id?: string | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface FamilyDocumentMeta {
  id: string;
  family_id: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  tags: string[];
  expires_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface FamilyDocument extends FamilyDocumentMeta {
  content_cipher: string;
  content_iv: string;
}

export interface FamilyWealthSnapshot {
  snapshot_date: string;
  total_assets: number;
  total_liabilities: number;
  net_worth: number;
}

export interface FamilyVisibilitySettings {
  modules: Record<string, { blockedRoles: string[] }>;
  /** Show personal data to family members */
  show_personal_data_to_family?: boolean;
  /** Show family data to members */
  show_family_data_to_members?: boolean;
  /** Allow external data sharing */
  allow_external_data_sharing?: boolean;
}

export interface FamilySecuritySettings {
  require_mfa: boolean;
  updated_by: string | null;
  updated_at: string | null;
}

export interface FamilyRolePermissions {
  canViewAssets: boolean;
  canAddTransactions: boolean;
  canViewReports: boolean;
  canEditGoals: boolean;
}

export interface FamilyNotificationSettings {
  goal_updates: boolean;
  expense_alerts: boolean;
  family_activity: boolean;
}

export interface FamilyAutomationSettings {
  report_frequency: "weekly" | "monthly" | "quarterly" | "yearly";
  expense_categories: boolean;
  goal_tracking: boolean;
}

export interface FamilyInvitationAuditEntry {
  id: string;
  family_id: string;
  invitation_id: string;
  actor_user_id: string | null;
  action: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface FamilyTransferTemplate {
  id: string;
  family_id: string;
  name: string;
  template: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface FamilySecureShare {
  id: string;
  family_id: string;
  scope: string;
  label: string | null;
  expires_at: string;
  created_at: string;
  revoked_at: string | null;
  share_token?: string;
}

export interface FamilyGoalShareLink {
  id: string;
  family_id: string;
  goal_id: string;
  expires_at: string;
  created_at: string;
  revoked_at: string | null;
  share_token?: string;
}

export interface FamilyDocumentVersion {
  id: string;
  source_document_id: string;
  document_id: string | null;
  family_id: string;
  version_number: number;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  created_at: string;
  created_by: string | null;
}

export interface FamilyComment {
  id: string;
  family_id: string;
  entity_type: string;
  entity_id: string;
  body: string;
  created_by: string;
  created_at: string;
  updated_at?: string;
}

export interface FamilyGoalSharePublic {
  link: {
    id: string;
    family_id: string;
    goal_id: string;
    expires_at: string;
    revoked_at: string | null;
  };
  goal: {
    id: string;
    goal_name: string;
    target_amount: number;
    current_amount: number;
    target_date: string | null;
    currency: string;
  };
}

export interface FamilyAssetInventoryItem {
  id: string;
  family_id: string;
  owner_member_id: string | null;
  title: string;
  category: string | null;
  category_key?: string | null;
  vault_document_id?: string | null;
  value_estimate: number | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface FamilyData {
  family: Family | null;
  familyMembers: FamilyMember[];
  familyGoals: FamilyGoal[];
  familyLicense: FamilyLicense | null;
}

/**
 * Make authenticated API call
 */
const FAMILY_API_TIMEOUT_MS = 30_000;

/**
 * Link Caller Abort Signal.
 */
function linkCallerAbortSignal(
  controller: AbortController,
  callerSignal: AbortSignal | null | undefined
): (() => void) | null {
  if (!callerSignal) return null;
  const onCallerAbort = () => controller.abort();
  if (callerSignal.aborted) {
    controller.abort();
  } else {
    callerSignal.addEventListener("abort", onCallerAbort, { once: true });
  }
  return () => callerSignal.removeEventListener("abort", onCallerAbort);
}

function mapFamilyApiAbortError(
  error: unknown,
  callerSignal: AbortSignal | null | undefined
): never {
  if (error instanceof Error && error.name === "AbortError") {
    if (callerSignal?.aborted) throw error;
    throw new Error(`Request timeout after ${FAMILY_API_TIMEOUT_MS}ms`);
  }
  throw error;
}

/**
 * Throw Family Api Http Error.
 */
async function throwFamilyApiHttpError(endpoint: string, response: Response): Promise<never> {
  const errorData = await response
    .json()
    .catch(() => ({ error: "Unknown error", message: "Unknown error" }));
  const errorMessage =
    (typeof errorData === "string" ? errorData : null) ||
    errorData.error ||
    errorData.message ||
    `API call failed: ${response.statusText || response.status}`;

  const logPayload = {
    status: response.status,
    errorMessage,
  };
  if (
    isRateLimitError(errorMessage, response.status) ||
    isTransientClientFetchError(errorMessage)
  ) {
    console.warn("❌ API Error for endpoint:", endpoint, logPayload);
  } else {
    console.error("❌ API Error for endpoint:", endpoint, {
      ...logPayload,
      statusText: response.statusText,
      error: errorData,
    });
  }
  throw new Error(errorMessage);
}

/**
 * Api Call.
 */
async function apiCall<T>(
  endpoint: string,
  options: globalThis.RequestInit = {},
  retryCount = 0
): Promise<T> {
  const token = await getAuthToken();
  const headers = await resolveApiRequestHeaders(options, token);
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FAMILY_API_TIMEOUT_MS);
  const callerSignal = options.signal;
  const unlinkCallerAbort = linkCallerAbortSignal(controller, callerSignal);

  try {
    const response = await fetch(endpoint, {
      ...options,
      headers,
      credentials: "include", // Important for CSRF cookies
      signal: controller.signal,
    });

    if (response.status === 429 && retryCount < 2) {
      const delay = rateLimitRetryDelayMs(retryCount, response.headers.get("Retry-After"));
      console.warn(
        `[FamilyService] Rate limited (429) on ${endpoint}, retrying after ${Math.round(delay / 1000)}s`
      );
      await sleepMs(delay);
      return apiCall<T>(endpoint, options, retryCount + 1);
    }

    if (!response.ok) {
      await throwFamilyApiHttpError(endpoint, response);
    }

    return response.json();
  } catch (error: unknown) {
    mapFamilyApiAbortError(error, callerSignal);
  } finally {
    clearTimeout(timeoutId);
    unlinkCallerAbort?.();
  }
}

export const familyService = {
  /**
   * Get family group for authenticated user
   */
  async getFamilyGroup(): Promise<FamilyData> {
    const result = await apiCall<{ data: FamilyData | null }>(API_ENDPOINTS.FAMILY.GROUP, {
      method: "GET",
    });
    return (
      result.data ?? {
        family: null,
        familyMembers: [],
        familyGoals: [],
        familyLicense: null,
      }
    );
  },

  /**
   * Create a new family group
   */
  async createFamilyGroup(data: {
    familyName: string;
    primaryMember: {
      full_name: string;
      pan_number?: string;
      date_of_birth?: string;
      encrypted_pan_number?: string;
      pan_encryption_iv?: string;
      masked_pan?: string;
    };
  }): Promise<Family> {
    const result = await apiCall<{ data: Family }>(`${API_ENDPOINTS.FAMILY.MEMBERS}`, {
      method: "POST",
      body: JSON.stringify(data),
    });
    return result.data;
  },

  /**
   * Update family group
   */
  async updateFamilyGroup(id: string, updates: Partial<Family>): Promise<Family> {
    const result = await apiCall<{ data: Family }>(`${API_ENDPOINTS.FAMILY.MEMBERS}/${id}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
    return result.data;
  },

  /**
   * Delete family group
   */
  async deleteFamilyGroup(id: string): Promise<void> {
    await apiCall(`${API_ENDPOINTS.FAMILY.MEMBERS}/${id}`, {
      method: "DELETE",
    });
  },

  /**
   * Get family members
   */
  async getFamilyMembers(): Promise<FamilyMember[]> {
    const result = await apiCall<{ members: FamilyMember[] }>(API_ENDPOINTS.FAMILY.MEMBERS, {
      method: "GET",
    });
    return result.members || [];
  },

  /**
   * Invite a member to the family
   */
  async inviteMember(data: {
    email: string;
    full_name: string;
    relationship: string;
    role?: string;
  }): Promise<FamilyInvitation> {
    const result = await apiCall<{ invitation: FamilyInvitation }>(API_ENDPOINTS.FAMILY.INVITE, {
      method: "POST",
      body: JSON.stringify(data),
    });
    return result.invitation;
  },

  /**
   * Accept family invitation
   */
  async acceptInvite(token: string): Promise<FamilyMember> {
    const result = await apiCall<{ data: FamilyMember }>(
      `${API_ENDPOINTS.FAMILY.ACCEPT_INVITE}/${token}`,
      {
        method: "POST",
      }
    );
    return result.data;
  },

  /**
   * Remove a family member
   */
  async removeMember(memberId: string, familyId: string): Promise<void> {
    await apiCall(`${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/members/${memberId}`, {
      method: "DELETE",
    });
  },

  /**
   * Add a family member (with encryption support)
   * Note: Encryption should be done client-side before calling this method
   */
  async addMember(
    familyId: string,
    data: {
      full_name: string;
      email?: string;
      relationship: string;
      pan_number: string; // Original for validation
      encrypted_pan_number?: string;
      pan_encryption_iv?: string;
      masked_pan?: string;
      role: string;
      date_of_birth?: string;
      is_active?: boolean;
    }
  ): Promise<FamilyMember> {
    const result = await apiCall<{ data: FamilyMember }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/members`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    );
    return result.data;
  },

  /**
   * Update a family member
   * VERSION: 2025-01-10-FIX - Fixed endpoint construction
   */
  async updateMember(
    memberId: string,
    updates: Partial<FamilyMember>,
    familyId?: string
  ): Promise<FamilyMember> {
    console.warn("🔄 [UPDATE_MEMBER_V2] Starting updateMember with:", {
      memberId,
      hasFamilyId: !!familyId,
      updates,
    });

    // If familyId is not provided, fetch it
    let actualFamilyId = familyId;
    if (!actualFamilyId) {
      console.warn("🔄 [UPDATE_MEMBER_V2] FamilyId not provided, fetching...");
      let familyData: FamilyData;
      try {
        familyData = await this.getFamilyGroup();
        actualFamilyId = familyData?.family?.id;
        console.warn("🔄 [UPDATE_MEMBER_V2] Fetched familyId:", actualFamilyId);
      } catch (error) {
        console.error("❌ [UPDATE_MEMBER_V2] Failed to get family group:", error);
        throw new Error("Failed to load family data. Please refresh and try again.");
      }

      if (!actualFamilyId) {
        console.error("❌ [UPDATE_MEMBER_V2] Family ID not found in fetched data");
        throw new Error("Family ID not found");
      }
    }

    // CRITICAL: Construct endpoint with explicit member path
    // OLD (WRONG): /api/v1/families/{familyId}
    // NEW (CORRECT): /api/v1/families/{familyId}/members/{memberId}
    const baseUrl = API_ENDPOINTS.FAMILY.MEMBERS; // /api/v1/families
    const endpoint = `${baseUrl}/${actualFamilyId}/members/${memberId}`;

    console.warn("🔧 [UPDATE_MEMBER_V2] Constructed endpoint:", {
      memberId,
      familyId: actualFamilyId,
      baseUrl,
      endpoint,
      fullUrl: endpoint,
    });

    // CRITICAL VALIDATION: Ensure endpoint has /members/ in it
    if (!endpoint.includes("/members/")) {
      const errorMsg = `❌ [UPDATE_MEMBER_V2] CRITICAL ERROR: Endpoint missing /members/ path: ${endpoint}`;
      console.error(errorMsg);
      throw new Error(
        `Invalid endpoint format: ${endpoint}. Expected: /api/v1/families/{familyId}/members/{memberId}`
      );
    }

    console.warn("✅ [UPDATE_MEMBER_V2] Making API call to:", endpoint);

    const result = await apiCall<{ data: FamilyMember }>(endpoint, {
      method: "PUT",
      body: JSON.stringify(updates),
    });

    console.warn("✅ [UPDATE_MEMBER_V2] API call successful:", result);
    return result.data;
  },

  /**
   * Archive a family member (set is_active to false)
   */
  archiveMember(memberId: string, familyId?: string): Promise<FamilyMember> {
    return this.updateMember(memberId, { is_active: false }, familyId);
  },

  /**
   * Update member role
   */
  async updateMemberRole(memberId: string, role: string): Promise<FamilyMember> {
    const result = await apiCall<{ member: FamilyMember }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${memberId}/role`,
      {
        method: "PUT",
        body: JSON.stringify({ role }),
      }
    );
    return result.member;
  },

  /**
   * Get family dashboard data
   */
  async getFamilyDashboard(familyId: string): Promise<FamilyDashboard> {
    const result = await apiCall<{ data: FamilyDashboard }>(
      `${API_ENDPOINTS.FAMILY.DASHBOARD}/${familyId}/stats`,
      {
        method: "GET",
      }
    );
    return result.data;
  },

  /**
   * Check whether the family can add more members (license / plan limit).
   */
  async checkMemberLimit(familyId: string): Promise<MemberLimitCheck> {
    const result = await apiCall<{ data: MemberLimitCheck }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/member-limit`,
      {
        method: "GET",
      }
    );
    return result.data;
  },

  /**
   * Get shared goals
   */
  async getSharedGoals(): Promise<FamilyGoal[]> {
    // Get goals from family data (family data includes goals)
    const familyData = await this.getFamilyGroup();
    return familyData.familyGoals || [];
  },

  /**
   * Create a family goal
   */
  async createFamilyGoal(
    familyId: string,
    goalData: {
      goal_name: string;
      target_amount: number;
      current_amount?: number;
      target_date?: string;
      category?: string;
      description?: string;
      currency?: string;
      owner_member_id?: string | null;
      priority?: string | null;
      monthly_contribution_amount?: number | null;
      sip_auto_enabled?: boolean | null;
      sip_day_of_month?: number | null;
    }
  ): Promise<FamilyGoal> {
    // Use FAMILY.GOALS base endpoint, not GROUP (which includes /me)
    const result = await apiCall<{ data: FamilyGoal }>(
      `${API_ENDPOINTS.FAMILY.GOALS}/${familyId}/goals`,
      {
        method: "POST",
        body: JSON.stringify(goalData),
      }
    );
    return result.data;
  },

  /**
   * Update a family goal
   */
  async updateFamilyGoal(
    familyId: string,
    goalId: string,
    updates: Partial<{
      goal_name: string;
      target_amount: number;
      current_amount: number;
      target_date: string;
      category: string;
      description: string;
      currency: string;
      is_archived: boolean;
      owner_member_id: string | null;
      last_milestone_percent: number | null;
      priority?: string | null;
      monthly_contribution_amount?: number | null;
      sip_auto_enabled?: boolean | null;
      sip_day_of_month?: number | null;
    }>
  ): Promise<FamilyGoal> {
    const result = await apiCall<{ data: FamilyGoal }>(
      `${API_ENDPOINTS.FAMILY.GOALS}/${familyId}/goals/${goalId}`,
      {
        method: "PUT",
        body: JSON.stringify(updates),
      }
    );
    return result.data;
  },

  /**
   * Archive a family goal
   */
  archiveFamilyGoal(familyId: string, goalId: string): Promise<FamilyGoal> {
    return this.updateFamilyGoal(familyId, goalId, { is_archived: true });
  },

  /**
   * Unarchive a family goal
   */
  unarchiveFamilyGoal(familyId: string, goalId: string): Promise<FamilyGoal> {
    return this.updateFamilyGoal(familyId, goalId, { is_archived: false });
  },

  /**
   * Permanently delete a family goal
   */
  async deleteFamilyGoal(familyId: string, goalId: string): Promise<void> {
    await apiCall(`${API_ENDPOINTS.FAMILY.GOALS}/${familyId}/goals/${goalId}`, {
      method: "DELETE",
    });
  },

  async listFamilyGoalContributions(familyId: string): Promise<FamilyGoalContribution[]> {
    const result = await apiCall<{ data: FamilyGoalContribution[] }>(
      `${API_ENDPOINTS.FAMILY.GOALS}/${familyId}/goals/contributions`,
      { method: "GET" }
    );
    return result.data;
  },

  async upsertGoalContribution(
    familyId: string,
    goalId: string,
    payload: {
      member_id: string;
      actual_contribution: number;
      contribution_percentage?: number | null;
    }
  ): Promise<{ contribution: FamilyGoalContribution; goal: FamilyGoal }> {
    const result = await apiCall<{
      data: { contribution: FamilyGoalContribution; goal: FamilyGoal };
    }>(`${API_ENDPOINTS.FAMILY.GOALS}/${familyId}/goals/${goalId}/contributions`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
    return result.data;
  },

  /**
   * List family calendar events
   */
  async listFamilyEvents(
    familyId: string,
    params?: { from?: string; to?: string }
  ): Promise<FamilyEvent[]> {
    const search = new URLSearchParams();
    if (params?.from) search.set("from", params.from);
    if (params?.to) search.set("to", params.to);
    const qs = search.toString();

    const result = await apiCall<{ data: FamilyEvent[] }>(
      appendQueryString(`${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/events`, qs),
      { method: "GET" }
    );
    return result.data;
  },

  async syncFamilyEventsFromFinancialData(familyId: string): Promise<FamilyCalendarSyncResult> {
    const result = await apiCall<{ data: FamilyCalendarSyncResult }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/events/sync`,
      { method: "POST" }
    );
    return result.data;
  },

  /**
   * Create family calendar event (optionally with reminders)
   */
  async createFamilyEvent(
    familyId: string,
    data: {
      title: string;
      description?: string;
      start_at: string;
      end_at?: string | null;
      all_day?: boolean;
      event_type?: FamilyEvent["event_type"];
      reminders?: { minutes_before: number; channel?: FamilyEventReminder["channel"] }[];
    }
  ): Promise<FamilyEvent> {
    const result = await apiCall<{ data: FamilyEvent }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/events`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    );
    return result.data;
  },

  /**
   * Update family calendar event (optionally replacing reminders)
   */
  async updateFamilyEvent(
    familyId: string,
    eventId: string,
    updates: Partial<{
      title: string;
      description: string;
      start_at: string;
      end_at: string | null;
      all_day: boolean;
      event_type: FamilyEvent["event_type"];
      reminders: { minutes_before: number; channel?: FamilyEventReminder["channel"] }[];
    }>
  ): Promise<FamilyEvent> {
    const result = await apiCall<{ data: FamilyEvent }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/events/${eventId}`,
      {
        method: "PUT",
        body: JSON.stringify(updates),
      }
    );
    return result.data;
  },

  /**
   * Delete family calendar event
   */
  async deleteFamilyEvent(familyId: string, eventId: string): Promise<void> {
    await apiCall(`${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/events/${eventId}`, {
      method: "DELETE",
    });
  },

  async getReminderPreferences(familyId: string): Promise<FamilyReminderPreferences> {
    const result = await apiCall<{ data: FamilyReminderPreferences }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/reminder-preferences`,
      { method: "GET" }
    );
    return result.data;
  },

  async updateReminderPreferences(
    familyId: string,
    prefs: Partial<Pick<FamilyReminderPreferences, "timezone" | "channels">>
  ): Promise<FamilyReminderPreferences> {
    const result = await apiCall<{ data: FamilyReminderPreferences }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/reminder-preferences`,
      {
        method: "PUT",
        body: JSON.stringify(prefs),
      }
    );
    return result.data;
  },

  async markMemberDeceased(input: {
    memberId: string;
    deceasedAt?: string;
    reason?: string;
    notes?: string;
    mfaCode?: string;
  }): Promise<{ success: boolean }> {
    const result = await apiCall<{ success: boolean }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/members/mark-deceased`,
      {
        method: "POST",
        body: JSON.stringify({
          memberId: input.memberId,
          deceasedAt: input.deceasedAt,
          reason: input.reason,
          notes: input.notes,
          mfaCode: input.mfaCode,
        }),
      }
    );
    return result;
  },

  async listFamilyTasks(
    familyId: string,
    params?: { status?: FamilyTask["status"]; assigned_to?: string }
  ): Promise<FamilyTask[]> {
    const search = new URLSearchParams();
    if (params?.status) search.set("status", params.status);
    if (params?.assigned_to) search.set("assigned_to", params.assigned_to);
    const qs = search.toString();

    const result = await apiCall<{ data: FamilyTask[] }>(
      appendQueryString(`${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/tasks`, qs),
      { method: "GET" }
    );
    return result.data;
  },

  async createFamilyTask(
    familyId: string,
    data: {
      title: string;
      description?: string;
      due_at?: string | null;
      assigned_to?: string | null;
      requires_approval?: boolean;
      linked_workflow?: string;
    }
  ): Promise<FamilyTask> {
    const result = await apiCall<{ data: FamilyTask }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/tasks`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    );
    return result.data;
  },

  async submitFamilyTask(familyId: string, taskId: string): Promise<FamilyTask> {
    const result = await apiCall<{ data: FamilyTask }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/tasks/${taskId}/submit`,
      { method: "POST" }
    );
    return result.data;
  },

  async approveFamilyTask(familyId: string, taskId: string): Promise<FamilyTask> {
    const result = await apiCall<{ data: FamilyTask }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/tasks/${taskId}/approve`,
      { method: "POST" }
    );
    return result.data;
  },

  async rejectFamilyTask(familyId: string, taskId: string): Promise<FamilyTask> {
    const result = await apiCall<{ data: FamilyTask }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/tasks/${taskId}/reject`,
      { method: "POST" }
    );
    return result.data;
  },

  async disputeFamilyTask(familyId: string, taskId: string, reason: string): Promise<FamilyTask> {
    const result = await apiCall<{ data: FamilyTask }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/tasks/${taskId}/dispute`,
      { method: "POST", body: JSON.stringify({ reason }) }
    );
    return result.data;
  },

  async completeFamilyTask(familyId: string, taskId: string): Promise<FamilyTask> {
    const result = await apiCall<{ data: FamilyTask }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/tasks/${taskId}/complete`,
      { method: "POST" }
    );
    return result.data;
  },

  async listFamilyNotes(familyId: string, params?: { query?: string }): Promise<FamilyNote[]> {
    const search = new URLSearchParams();
    if (params?.query) search.set("query", params.query);
    const qs = search.toString();

    const result = await apiCall<{ data: FamilyNote[] }>(
      appendQueryString(`${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/notes`, qs),
      { method: "GET" }
    );
    return result.data;
  },

  async createFamilyNote(
    familyId: string,
    data: Pick<FamilyNote, "title" | "content_md" | "visibility" | "note_type" | "tags"> &
      Partial<Pick<FamilyNote, "is_pinned" | "linked_entity_type" | "linked_entity_id">>
  ): Promise<FamilyNote> {
    const result = await apiCall<{ data: FamilyNote }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/notes`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    );
    return result.data;
  },

  async updateFamilyNote(
    familyId: string,
    noteId: string,
    updates: Partial<
      Pick<
        FamilyNote,
        | "title"
        | "content_md"
        | "visibility"
        | "note_type"
        | "tags"
        | "is_pinned"
        | "linked_entity_type"
        | "linked_entity_id"
      >
    >
  ): Promise<FamilyNote> {
    const result = await apiCall<{ data: FamilyNote }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/notes/${noteId}`,
      {
        method: "PUT",
        body: JSON.stringify(updates),
      }
    );
    return result.data;
  },

  async deleteFamilyNote(familyId: string, noteId: string): Promise<void> {
    await apiCall(`${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/notes/${noteId}`, {
      method: "DELETE",
    });
  },

  async listFamilyDocuments(familyId: string): Promise<FamilyDocumentMeta[]> {
    const result = await apiCall<{ data: FamilyDocumentMeta[] }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/documents`,
      { method: "GET" }
    );
    return result.data;
  },

  async uploadFamilyDocument(
    familyId: string,
    data: Pick<
      FamilyDocument,
      | "file_name"
      | "mime_type"
      | "size_bytes"
      | "content_cipher"
      | "content_iv"
      | "tags"
      | "expires_at"
    >,
    opts?: { mfaCode?: string }
  ): Promise<FamilyDocumentMeta> {
    const payload = opts?.mfaCode ? { ...data, mfaCode: opts.mfaCode } : data;
    const result = await apiCall<{ data: FamilyDocumentMeta }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/documents`,
      { method: "POST", body: JSON.stringify(payload) }
    );
    return result.data;
  },

  async getFamilyDocument(
    familyId: string,
    docId: string,
    opts?: { mfaCode?: string }
  ): Promise<FamilyDocument> {
    const result = await apiCall<{ data: FamilyDocument }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/documents/${docId}`,
      { method: "GET", headers: opts?.mfaCode ? { "x-mfa-code": opts.mfaCode } : undefined }
    );
    return result.data;
  },

  async deleteFamilyDocument(
    familyId: string,
    docId: string,
    opts?: { mfaCode?: string }
  ): Promise<void> {
    await apiCall(`${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/documents/${docId}`, {
      method: "DELETE",
      headers: opts?.mfaCode ? { "x-mfa-code": opts.mfaCode } : undefined,
    });
  },

  async getFamilyWealthSnapshots(
    familyId: string,
    params?: { from?: string; to?: string }
  ): Promise<FamilyWealthSnapshot[]> {
    const search = new URLSearchParams();
    if (params?.from) search.set("from", params.from);
    if (params?.to) search.set("to", params.to);
    const qs = search.toString();

    const result = await apiCall<{ data: FamilyWealthSnapshot[] }>(
      appendQueryString(`${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/wealth/snapshots`, qs),
      { method: "GET" }
    );
    return result.data;
  },

  async getFamilyVisibilitySettings(familyId: string): Promise<FamilyVisibilitySettings> {
    const result = await apiCall<{ data: FamilyVisibilitySettings }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/visibility-settings`,
      { method: "GET" }
    );
    return result.data;
  },

  async updateFamilyVisibilitySettings(
    familyId: string,
    settings: Partial<FamilyVisibilitySettings>
  ): Promise<FamilyVisibilitySettings> {
    const result = await apiCall<{ data: FamilyVisibilitySettings }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/visibility-settings`,
      { method: "PUT", body: JSON.stringify(settings) }
    );
    return result.data;
  },

  async getFamilySecuritySettings(familyId: string): Promise<FamilySecuritySettings> {
    const result = await apiCall<{ data: FamilySecuritySettings }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/security-settings`,
      { method: "GET" }
    );
    return result.data;
  },

  async updateFamilySecuritySettings(
    familyId: string,
    settings: Pick<FamilySecuritySettings, "require_mfa">
  ): Promise<FamilySecuritySettings> {
    const result = await apiCall<{ data: FamilySecuritySettings }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/security-settings`,
      { method: "PUT", body: JSON.stringify(settings) }
    );
    return result.data;
  },

  async getFamilyRolePermissions(familyId: string): Promise<FamilyRolePermissions> {
    const result = await apiCall<{ data: FamilyRolePermissions }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/role-permissions`,
      { method: "GET" }
    );
    return result.data;
  },

  async updateFamilyRolePermissions(
    familyId: string,
    settings: Partial<FamilyRolePermissions>
  ): Promise<FamilyRolePermissions> {
    const result = await apiCall<{ data: FamilyRolePermissions }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/role-permissions`,
      { method: "PUT", body: JSON.stringify(settings) }
    );
    return result.data;
  },

  async getFamilyNotificationSettings(familyId: string): Promise<FamilyNotificationSettings> {
    const result = await apiCall<{ data: FamilyNotificationSettings }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/notification-settings`,
      { method: "GET" }
    );
    return result.data;
  },

  async updateFamilyNotificationSettings(
    familyId: string,
    settings: Partial<FamilyNotificationSettings>
  ): Promise<FamilyNotificationSettings> {
    const result = await apiCall<{ data: FamilyNotificationSettings }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/notification-settings`,
      { method: "PUT", body: JSON.stringify(settings) }
    );
    return result.data;
  },

  async getFamilyAutomationSettings(familyId: string): Promise<FamilyAutomationSettings> {
    const result = await apiCall<{ data: FamilyAutomationSettings }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/automation-settings`,
      { method: "GET" }
    );
    return result.data;
  },

  async updateFamilyAutomationSettings(
    familyId: string,
    settings: Partial<FamilyAutomationSettings>
  ): Promise<FamilyAutomationSettings> {
    const result = await apiCall<{ data: FamilyAutomationSettings }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/automation-settings`,
      { method: "PUT", body: JSON.stringify(settings) }
    );
    return result.data;
  },

  async getFamilyAuditLog(
    familyId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<{ items: FamilyInvitationAuditEntry[]; total: number; hasMore: boolean }> {
    const search = new URLSearchParams();
    if (params?.limit !== undefined) search.set("limit", String(params.limit));
    if (params?.offset !== undefined) search.set("offset", String(params.offset));
    const query = search.toString();
    const result = await apiCall<{
      data: { items: FamilyInvitationAuditEntry[]; total: number; hasMore: boolean };
    }>(appendQueryString(`${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/audit`, query), {
      method: "GET",
    });
    return result.data;
  },

  async listFamilyGoals(
    familyId: string,
    params?: { limit?: number; offset?: number; includeArchived?: boolean; archivedOnly?: boolean }
  ): Promise<{ goals: FamilyGoal[]; total: number; hasMore: boolean }> {
    const search = new URLSearchParams();
    if (params?.limit !== undefined) search.set("limit", String(params.limit));
    if (params?.offset !== undefined) search.set("offset", String(params.offset));
    if (params?.includeArchived) search.set("include_archived", "true");
    if (params?.archivedOnly) search.set("archived_only", "true");
    const query = search.toString();
    const result = await apiCall<{
      data: { goals: FamilyGoal[]; total: number; hasMore: boolean };
    }>(appendQueryString(`${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/goals`, query), {
      method: "GET",
    });
    return result.data;
  },

  exportFamilyData(
    familyId: string,
    opts?: { mfaCode?: string }
  ): Promise<Record<string, unknown>> {
    return apiCall<Record<string, unknown>>(`${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/export`, {
      method: "GET",
      headers: opts?.mfaCode ? { "x-mfa-code": opts.mfaCode } : undefined,
    });
  },

  async listTransferTemplates(familyId: string): Promise<FamilyTransferTemplate[]> {
    const result = await apiCall<{ data: FamilyTransferTemplate[] }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/transfer-templates`,
      { method: "GET" }
    );
    return result.data;
  },

  async createTransferTemplate(
    familyId: string,
    input: Pick<FamilyTransferTemplate, "name" | "template">,
    opts?: { mfaCode?: string }
  ): Promise<FamilyTransferTemplate> {
    const payload = opts?.mfaCode ? { ...input, mfaCode: opts.mfaCode } : input;
    const result = await apiCall<{ data: FamilyTransferTemplate }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/transfer-templates`,
      { method: "POST", body: JSON.stringify(payload) }
    );
    return result.data;
  },

  async updateTransferTemplate(
    familyId: string,
    templateId: string,
    updates: Partial<Pick<FamilyTransferTemplate, "name" | "template">>,
    opts?: { mfaCode?: string }
  ): Promise<FamilyTransferTemplate> {
    const payload = opts?.mfaCode ? { ...updates, mfaCode: opts.mfaCode } : updates;
    const result = await apiCall<{ data: FamilyTransferTemplate }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/transfer-templates/${templateId}`,
      { method: "PUT", body: JSON.stringify(payload) }
    );
    return result.data;
  },

  async deleteTransferTemplate(
    familyId: string,
    templateId: string,
    opts?: { mfaCode?: string }
  ): Promise<void> {
    await apiCall(`${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/transfer-templates/${templateId}`, {
      method: "DELETE",
      headers: opts?.mfaCode ? { "x-mfa-code": opts.mfaCode } : undefined,
    });
  },

  async listAssetInventoryItems(familyId: string): Promise<FamilyAssetInventoryItem[]> {
    const result = await apiCall<{ data: FamilyAssetInventoryItem[] }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/inventory`,
      { method: "GET" }
    );
    return result.data;
  },

  async createAssetInventoryItem(
    familyId: string,
    input: Pick<
      FamilyAssetInventoryItem,
      | "title"
      | "category"
      | "category_key"
      | "vault_document_id"
      | "value_estimate"
      | "notes"
      | "owner_member_id"
    >,
    opts?: { mfaCode?: string }
  ): Promise<FamilyAssetInventoryItem> {
    const payload = opts?.mfaCode ? { ...input, mfaCode: opts.mfaCode } : input;
    const result = await apiCall<{ data: FamilyAssetInventoryItem }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/inventory`,
      { method: "POST", body: JSON.stringify(payload) }
    );
    return result.data;
  },

  async updateAssetInventoryItem(
    familyId: string,
    itemId: string,
    updates: Partial<
      Pick<
        FamilyAssetInventoryItem,
        | "title"
        | "category"
        | "category_key"
        | "vault_document_id"
        | "value_estimate"
        | "notes"
        | "owner_member_id"
      >
    >,
    opts?: { mfaCode?: string }
  ): Promise<FamilyAssetInventoryItem> {
    const payload = opts?.mfaCode ? { ...updates, mfaCode: opts.mfaCode } : updates;
    const result = await apiCall<{ data: FamilyAssetInventoryItem }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/inventory/${itemId}`,
      { method: "PUT", body: JSON.stringify(payload) }
    );
    return result.data;
  },

  async deleteAssetInventoryItem(
    familyId: string,
    itemId: string,
    opts?: { mfaCode?: string }
  ): Promise<void> {
    await apiCall(`${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/inventory/${itemId}`, {
      method: "DELETE",
      headers: opts?.mfaCode ? { "x-mfa-code": opts.mfaCode } : undefined,
    });
  },

  async sendFamilyDigest(familyId: string): Promise<void> {
    await apiCall(`${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/notifications/digest`, {
      method: "POST",
    });
  },

  async sendFamilyReport(familyId: string): Promise<void> {
    await apiCall(`${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/reports/generate`, {
      method: "POST",
    });
  },

  async createSecureShare(
    familyId: string,
    input: { scope?: string; label?: string; expires_at?: string }
  ): Promise<FamilySecureShare> {
    const result = await apiCall<{ data: FamilySecureShare }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/secure-shares`,
      { method: "POST", body: JSON.stringify(input) }
    );
    return result.data;
  },

  async listSecureShares(familyId: string): Promise<FamilySecureShare[]> {
    const result = await apiCall<{ data: FamilySecureShare[] }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/secure-shares`
    );
    return result.data;
  },

  async revokeSecureShare(familyId: string, shareId: string): Promise<FamilySecureShare> {
    const result = await apiCall<{ data: FamilySecureShare }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/secure-shares/${shareId}/revoke`,
      { method: "POST" }
    );
    return result.data;
  },

  async createGoalShareLink(familyId: string, goalId: string): Promise<FamilyGoalShareLink> {
    const result = await apiCall<{ data: FamilyGoalShareLink }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/goals/${goalId}/share-links`,
      { method: "POST", body: JSON.stringify({}) }
    );
    return result.data;
  },

  async listGoalShareLinks(familyId: string, goalId: string): Promise<FamilyGoalShareLink[]> {
    const result = await apiCall<{ data: FamilyGoalShareLink[] }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/goals/${goalId}/share-links`
    );
    return result.data;
  },

  async getGoalShareByToken(token: string): Promise<FamilyGoalSharePublic> {
    const response = await fetch(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/goal-share/${encodeURIComponent(token)}`,
      { credentials: "include" }
    );
    const body = (await response.json().catch(() => ({}))) as {
      data?: FamilyGoalSharePublic;
      message?: string;
    };
    if (!response.ok) {
      throw new Error(body.message || "Share link not found or expired");
    }
    if (!body.data) {
      throw new Error("Share link not found or expired");
    }
    return body.data;
  },

  async listDocumentVersions(
    familyId: string,
    documentId: string
  ): Promise<FamilyDocumentVersion[]> {
    const result = await apiCall<{ data: FamilyDocumentVersion[] }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/documents/${documentId}/versions`
    );
    return result.data;
  },

  async listFamilyComments(
    familyId: string,
    entityType: string,
    entityId: string
  ): Promise<FamilyComment[]> {
    const params = new URLSearchParams({
      entity_type: entityType,
      entity_id: entityId,
    });
    const result = await apiCall<{ data: FamilyComment[] }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/comments?${params.toString()}`
    );
    return result.data;
  },

  async createFamilyComment(
    familyId: string,
    input: { entity_type: string; entity_id: string; body: string }
  ): Promise<FamilyComment> {
    const result = await apiCall<{ data: FamilyComment }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/comments`,
      { method: "POST", body: JSON.stringify(input) }
    );
    return result.data;
  },

  async updateMemberGenerationTag(
    familyId: string,
    memberId: string,
    generationTag: string | null
  ): Promise<{ id: string; generation_tag: string | null }> {
    const result = await apiCall<{ data: { id: string; generation_tag: string | null } }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/members/${memberId}/generation-tag`,
      { method: "PATCH", body: JSON.stringify({ generation_tag: generationTag }) }
    );
    return result.data;
  },

  async getFamilySecurityAlerts(familyId: string): Promise<FamilyInvitationAuditEntry[]> {
    const result = await apiCall<{ data: FamilyInvitationAuditEntry[] }>(
      `${API_ENDPOINTS.FAMILY.MEMBERS}/${familyId}/security-alerts`
    );
    return result.data;
  },
};
